/**
 * 物理実機の絶対識別子 `device_unit_uid`。
 *
 * 人が編集する gateway_id と違い、 端末が機械生成し永続する UUID。 localStorage を主、
 * IndexedDB を冗長保存とし、 片方が消えても復元できるようにする（両方消去＝工場リセット等で
 * 再生成＝新実機扱い、 は許容）。 UI には出さない。
 *
 * 設計: adbox-admin-rails docs/design/features/device_telemetry.md
 */

const LS_KEY = "deviceUnitUid";
const IDB_NAME = "adbox";
const IDB_STORE = "kv";
const IDB_KEY = "deviceUnitUid";

interface StorageLike {
  getStorageSync: <T = unknown>(key: string) => T;
  setStorageSync: (key: string, value: unknown) => void;
}

const generateUid = (): string => {
  // crypto.randomUUID は TS lib のバージョン差があるため、 存在チェック付きで参照する。
  const cryptoObj =
    typeof crypto !== "undefined"
      ? (crypto as Crypto & { randomUUID?: () => string })
      : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  // 非セキュアコンテキスト向けフォールバック (通常は本番 https で randomUUID が使える)。
  return `uid-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const idbGet = async (): Promise<string | null> => {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const r = db
        .transaction(IDB_STORE, "readonly")
        .objectStore(IDB_STORE)
        .get(IDB_KEY);
      r.onsuccess = () => resolve((r.result as string) ?? null);
      r.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
};

const idbSet = async (value: string): Promise<void> => {
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(value, IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    /* best-effort */
  }
};

/**
 * device_unit_uid を取得（無ければ生成）。 localStorage / IndexedDB の両方へ冗長保存する。
 * どちらかに残っていれば復元、 両方無ければ新規生成。
 */
export const getDeviceUnitUid = async (
  storage: StorageLike
): Promise<string> => {
  const fromLs = storage.getStorageSync<string>(LS_KEY);
  if (fromLs) {
    void idbSet(fromLs); // IndexedDB 側を補完
    return fromLs;
  }

  const fromIdb = await idbGet();
  if (fromIdb) {
    storage.setStorageSync(LS_KEY, fromIdb); // localStorage 側を復元
    return fromIdb;
  }

  const uid = generateUid();
  storage.setStorageSync(LS_KEY, uid);
  void idbSet(uid);
  return uid;
};
